import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../../../components/layout/AppLayout';
import { useCourseApi } from '../../../../api/courses/courses';
import { TLesson } from '../../../../models/courses/lesson';
import AIModelBuilderLessonAdmin from '../../../../components/AIModelBuilderLesson/admin/AIModelBuilderLessonAdmin';
import AIModelBuilderLessonTeacher from '../../../../components/AIModelBuilderLesson/teacher/AIModelBuilderLessonTeacher';
import {
  TAIModelBuilderLessonConfig,
  createEmptyAIModelBuilderLessonConfig,
} from '../../../../models/courses/aiModelBuilder';

const LessonAIModelBuilder: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getLessonDetailsById, saveLessonDetails } = useCourseApi();

  const stateLesson: TLesson | undefined = location.state?.lesson;
  const teacherMode: boolean = location.state?.teacherMode || false;
  const epic = location.state?.epic;

  const [editingLesson, setEditingLesson] = useState<TLesson | null>(stateLesson ?? null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importedStatement, setImportedStatement] = useState<string>('');

  const lessonIdFromUrl = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const lessonId = params.get('lessonId');
    return lessonId ? parseInt(lessonId, 10) : undefined;
  }, [location.search]);

  useEffect(() => {
    const lessonId = lessonIdFromUrl ?? stateLesson?.id;
    if (!lessonId) {
      message.warning(t('aiModelBuilder.page.messages.selectLesson'));
      navigate('/setup/lesson', epic ? { state: { epic } } : undefined);
      return;
    }
    setLoading(true);
    getLessonDetailsById(lessonId)
      .then((response) => {
        const lessonData = response.data;
        const existingConfig = lessonData.config?.config as TAIModelBuilderLessonConfig | undefined;
        lessonData.config = {
          id: lessonData.config?.id ?? 0,
          lesson_id: lessonData.id,
          config: existingConfig?.dataset
            ? existingConfig
            : createEmptyAIModelBuilderLessonConfig(),
        };
        setEditingLesson(lessonData);
        setImportedStatement(lessonData.statement || '');
      })
      .catch((error) => {
        message.error(error.response?.data ?? error.message);
        navigate('/setup/lesson', epic ? { state: { epic } } : undefined);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line 
  }, []);

  const currentConfig: TAIModelBuilderLessonConfig = useMemo(() => {
    const rawConfig = editingLesson?.config?.config as TAIModelBuilderLessonConfig | undefined;
    if (rawConfig?.dataset) {
      return rawConfig;
    }
    return createEmptyAIModelBuilderLessonConfig();
  }, [editingLesson]);

  const handleConfigChange = (config: TAIModelBuilderLessonConfig) => {
    setEditingLesson((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        config: {
          id: prev.config?.id ?? 0,
          lesson_id: prev.id,
          config,
        },
      };
    });
  };

  const handleSave = async () => {
    if (!editingLesson) {
      return;
    }
    setSaving(true);
    try {
      const payload: TLesson = {
        ...editingLesson,
        statement: importedStatement || editingLesson.statement || '',
        config: {
          id: editingLesson.config?.id ?? 0,
          lesson_id: editingLesson.id,
          config: {
            ...(editingLesson.config?.config as TAIModelBuilderLessonConfig),
            statement: importedStatement || editingLesson.statement || '',
          },
        },
      };
      await saveLessonDetails(payload);
      message.success(t('aiModelBuilder.page.messages.saveSuccess'));
    } catch (error: any) {
      message.error(error.response?.data ?? error.message ?? error);
    } finally {
      setSaving(false);
    }
  };

  const handleBackNavigation = () => {
    navigate(-1);
  };

  return (
    <AppLayout
      title={editingLesson?.name || t('aiModelBuilder.page.title')}
      onBackNavigation={handleBackNavigation}
      showBackButton
      loading={loading}
    >
      {editingLesson && (
        teacherMode ? (
          <AIModelBuilderLessonTeacher
            config={currentConfig}
            statement={editingLesson.statement || currentConfig.statement}
          />
        ) : (
          <AIModelBuilderLessonAdmin
            config={currentConfig}
            onConfigChange={handleConfigChange}
            onSave={handleSave}
            saving={saving}
            initialStatement={importedStatement}
            onStatementChange={setImportedStatement}
          />
        )
      )}
    </AppLayout>
  );
};

export default LessonAIModelBuilder;
